/* eslint-disable no-nested-ternary */

/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import TabContext from "@mui/lab/TabContext";
import TabPanel from "@mui/lab/TabPanel";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

import Icons from "components/CustomIcons";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSlider from "components/MDSlider";
import FormCheckbox from "components/FormCheckbox";
import FormTimePicker from "components/FormTimePicker";

import { useTranslation } from "react-i18next";
import { Field, reduxForm, formValueSelector } from "redux-form";
import { useMaterialUIController } from "context";
import moment from "moment";

// NewUser page components

const validate = (values) => {
  const errors = {};
  const requiredFields = ["deviceId"];
  requiredFields.forEach((field) => {
    if (!values[field]) {
      errors[field] = "Required";
    }
  });
  return errors;
};

const pctToByte = (v) => Math.round(v * 2.55);
const byteToPct = (v) => Math.round(v / 2.55);

function EventAddEditForm(props) {
  const {
    onSubmit,
    actionType,
    devices,
    deviceNodeId,
    selectedEvent,
    weekDay,
    change,
    scheduled,
    eventTime,
    thermostatIPReducer = {},
  } = props;

  const { id, value } = selectedEvent;
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [deviceToEdit, setDeviceToEdit] = useState(null);
  const [onOffValue, setOnOffValue] = useState(0);
  const [analogValue, setAnalogValue] = useState(0);

  const [rVal, setRVal] = useState(0);
  const [gVal, setGVal] = useState(0);
  const [bVal, setBVal] = useState(0);
  const [whiteVal, setWhiteVal] = useState(0);
  const [intensityVal, setIntensityVal] = useState(0);
  const [rgbTab, setRgbTab] = useState("color");
  useEffect(() => {
    devices.forEach((device) => {
      if (device.nodeId === deviceNodeId) setDeviceToEdit(device);
    });
  }, [deviceNodeId]);

  useEffect(() => {
    devices.forEach((device) => {
      if (device.nodeId === deviceNodeId) setDeviceToEdit(device);
    });
  }, [JSON.stringify(selectedEvent)]);

  useEffect(() => {
    if (!deviceToEdit) return;
    const { type } = deviceToEdit;
    if (type === "device15") {
      const th = thermostatIPReducer?.thermostats?.[deviceNodeId];
      if (value === undefined && th) {
        setOnOffValue(th.onOff);
        setAnalogValue(parseFloat(th.setpoint));
        return;
      }
    }
    if (type === "device3" || type === "device4") {
      const is4 = type === "device4";

      if (Array.isArray(value)) {
        value.forEach(({ featureType, featureValue }) => {
          if (featureType !== "color") return;

          if (!is4 && Array.isArray(featureValue)) {
            setRVal(featureValue[0] ?? 0);
            setGVal(featureValue[1] ?? 0);
            setBVal(featureValue[2] ?? 0);
          }

          if (is4 && typeof featureValue === "object" && featureValue !== null) {
            setRVal(byteToPct(featureValue.r ?? 0));
            setGVal(byteToPct(featureValue.g ?? 0));
            setBVal(byteToPct(featureValue.b ?? 0));
          }
        });

        value.forEach(({ featureType, featureValue }) => {
          if (featureType === "white") setWhiteVal(featureValue);
          if (featureType === "intensity") setIntensityVal(featureValue);
          if (featureType === "onOff") setOnOffValue(featureValue);
        });
      } else {
        setOnOffValue(0);
        setRVal(0);
        setGVal(0);
        setBVal(0);
        setWhiteVal(0);
        setIntensityVal(0);
      }
      return;
    }

    if (!Array.isArray(value)) {
      if (value === undefined) {
        setOnOffValue(0);
        setAnalogValue(0);
      } else {
        setOnOffValue(value);
        setAnalogValue(parseFloat(value));
      }
    } else {
      value.forEach(({ featureType, featureValue }) => {
        if (featureType === "setpoint") setAnalogValue(featureValue);
        if (featureType === "onOff") setOnOffValue(featureValue);
      });
    }
  }, [deviceToEdit, selectedEvent, thermostatIPReducer, deviceNodeId, value]);

  const {
    type,
    value: deviceValue,
    onOffInverted,
    onOff,
    valueForOn,
    valueForOff,
    visualizationType,
    labelOn,
    labelOff,
    sufix,
    fixedValues,
    iconOn,
    iconOff,
    minValue = "7",
    maxValue = "40",
    step = "0.5",
    slowStep = "1",
    decimalPlaces,
  } = deviceToEdit || {};

  const isDeviceRGB = type === "device3" || type === "device4";
  const hasWhite = Boolean(deviceToEdit?.whiteValue);

  const getValueType = () => {
    // Device value
    let auxValue = 0;

    switch (type) {
      case "device1":
        auxValue = onOffValue;
        break;
      case "device2":
        auxValue = analogValue;
        break;
      case "device5":
        auxValue = deviceValue ? analogValue : onOffValue;
        break;
      case "device8":
        auxValue = onOffValue;
        break;
      case "device9":
        auxValue = analogValue;
        break;
      case "device10": {
        auxValue = [
          {
            featureType: "setpoint",
            featureValue: analogValue,
            featureSend: true,
            featureLabel: `${analogValue}ºC`,
          },
        ];
        if (onOff) {
          auxValue.push({
            featureType: "onOff",
            featureValue: onOffValue,
            featureSend: true,
            featureLabel: t(
              onOffInverted
                ? onOffValue
                  ? "labelOff"
                  : "labelOn"
                : onOffValue
                ? "labelOn"
                : "labelOff"
            ).toUpperCase(),
          });
        }
        break;
      }
      case "device15":
        auxValue = [
          {
            featureType: "setpoint",
            featureValue: analogValue,
            featureSend: true,
            featureLabel: `${analogValue}ºC`,
          },
          {
            featureType: "onOff",
            featureValue: onOffValue,
            featureSend: true,
            featureLabel: t(onOffValue ? "labelOn" : "labelOff").toUpperCase(),
          },
        ];
        break;

      case "device3":
      case "device4": {
        auxValue = [
          {
            featureType: "color",
            featureValue:
              type === "device3"
                ? [rVal, gVal, bVal]
                : {
                    b: pctToByte(bVal),
                    g: pctToByte(gVal),
                    r: pctToByte(rVal),
                  },
            featureSend: true,
            featureLabel: false,
          },
          {
            featureType: "intensity",
            featureValue: intensityVal,
            featureSend: true,
            featureLabel: `${intensityVal}%`,
          },
        ];

        if (hasWhite && whiteVal > 0) {
          auxValue.push({
            featureType: "white",
            featureValue: whiteVal,
            featureSend: true,
            featureLabel: `${whiteVal}%`,
          });
        }

        auxValue.push({
          featureType: "onOff",
          featureValue: onOffValue,
          featureSend: true,
          featureLabel: t(onOffValue ? "btnOn" : "btnOff").toUpperCase(),
        });

        break;
      }

      default:
        break;
    }

    return auxValue;
  };

  const getValueText = () => {
    let state = 0;

    const customAnalogFixedValue = (newValue) => {
      let msg = t("noMatch").toUpperCase();
      fixedValues.forEach((item) => {
        if (parseFloat(item.value) === Math.round(newValue)) {
          msg = item.label;
        }
      });
      return msg;
    };

    if (type === "device1") state = onOffValue ? t("btnOn") : t("btnOff");
    if (type === "device2") state = `${analogValue}%`;
    if (type === "device5")
      state = deviceValue ? `${analogValue}%` : onOffValue ? t("btnDown") : t("btnUp");
    if (type === "device8") {
      state =
        visualizationType === "label"
          ? onOffValue === parseInt(valueForOn, 10)
            ? labelOn
            : labelOff
          : onOffValue === parseInt(valueForOn, 10)
          ? t("btnOn")
          : t("btnOff");
    }
    if (type === "device9")
      state =
        visualizationType === "fixed"
          ? customAnalogFixedValue(analogValue)
          : `${analogValue}${sufix}`;
    if (type === "device10")
      state = `${analogValue}ºC, ${
        onOffInverted
          ? onOffValue
            ? t("labelOff")
            : t("labelOn")
          : onOffValue
          ? t("labelOn")
          : t("labelOff")
      }`;
    if (type === "device15") state = `${analogValue}ºC, ${t(onOffValue ? "labelOn" : "labelOff")}`;

    if (isDeviceRGB) {
      state = `RGB ${rVal}/${gVal}/${bVal}% · ${t("intensity")} ${intensityVal}%${
        hasWhite ? ` · ${t("whiteL")} ${whiteVal}%` : ""
      } · ${t(onOffValue ? "btnOn" : "btnOff")}`;
    }

    return state.toUpperCase();
  };

  const getValueColor = () => [rVal, gVal, bVal];

  const submitData = () => {
    if (!moment.isMoment(eventTime)) return;
    const hour = parseInt(eventTime.format("HH"), 10);
    const minute = parseInt(eventTime.format("m"), 10);

    onSubmit({
      day: "*",
      hour,
      id,
      minute,
      month: "*",
      scheduled,
      second: "0",
      sunrise: false,
      sunset: false,
      weekDay,
      nodeId: deviceNodeId,
      value: getValueType(),
      valueText: getValueText(),
      valueColor: getValueColor(),
    });
  };

  const getShutterIcon = () => {
    const { value: valueConfig } = deviceToEdit;
    if (valueConfig) {
      return analogValue === 100
        ? "WindowShutter"
        : analogValue === 0
        ? "WindowShutterOpen"
        : "WindowShutterHalf";
    }
    return onOffValue === 1 ? "WindowShutter" : "WindowShutterOpen";
  };

  const getIcon = () => {
    switch (type) {
      case "device1":
      case "device2":
      case "device3":
      case "device4":
        return (
          <Icons
            name={onOffValue ? "LightbulbOnOutline" : "LightbulbOutline"}
            size={40}
            color={darkMode ? "white" : "black"}
          />
        );
      case "device5":
        return <Icons name={getShutterIcon()} size={40} color={darkMode ? "white" : "black"} />;
      case "device6":
        return visualizationType === "icon" ? (
          <Icons
            name={onOffValue ? iconOn : iconOff}
            size={40}
            color={darkMode ? "white" : "black"}
          />
        ) : (
          <div />
        );
      case "device8":
        return visualizationType === "icon" ? (
          <Icons
            name={parseInt(valueForOn, 10) === onOffValue ? iconOn : iconOff}
            size={40}
            color={darkMode ? "white" : "black"}
          />
        ) : (
          <div />
        );
      case "device10":
      case "device15":
        return <Icons name="Thermometer" size={40} color={darkMode ? "white" : "black"} />;
      default:
        return <div />;
    }
  };

  const onOffChange = () => {
    switch (type) {
      case "device1":
      case "device10":
      case "device15":
      case "device3":
      case "device4":
        setOnOffValue(onOffValue ? 0 : 1);
        break;
      case "device8": {
        let newValue = 0;
        if (onOffValue === parseInt(valueForOn, 10) || onOffValue !== parseInt(valueForOff, 10)) {
          newValue = parseInt(valueForOff, 10);
        } else {
          newValue = parseInt(valueForOn, 10);
        }
        setOnOffValue(newValue);
        break;
      }
      default:
        break;
    }
  };

  const getControls = () => {
    switch (type) {
      case "device1":
      case "device10":
      case "device15":
      case "device3":
      case "device4":
        return (
          <Switch checked={onOffInverted ? !onOffValue : !!onOffValue} onChange={onOffChange} />
        );
      case "device8":
        return (
          <Switch
            checked={
              !!(
                onOffValue === parseInt(valueForOn, 10) || onOffValue !== parseInt(valueForOff, 10)
              )
            }
            onChange={() => {
              onOffChange();
            }}
          />
        );
      default:
        return <div />;
    }
  };

  const getValue = () => {
    if (type === "device2" || (type === "device5" && deviceValue)) {
      return (
        <MDBox mt={1} mx={2}>
          <MDSlider
            value={analogValue}
            onChange={(v) => setAnalogValue(v)}
            color="error"
            valueLabelDisplay="auto"
          />
        </MDBox>
      );
    }

    if (type === "device10" || type === "device15") {
      return (
        <MDBox minHeight={125}>
          <MDBox mt={1} mx={2}>
            <MDSlider
              value={analogValue}
              onChange={(v) => setAnalogValue(v)}
              min={parseFloat(minValue)}
              max={parseFloat(maxValue)}
              step={parseFloat(step)}
              color="error"
              valueLabelDisplay="auto"
            />
          </MDBox>
          <MDBox mb={1} display="flex" justifyContent="center">
            <MDTypography variant="h2">{`${analogValue.toFixed(1)}ºC`}</MDTypography>
          </MDBox>
        </MDBox>
      );
    }

    if (type === "device9") {
      return (
        <MDBox minHeight={125}>
          {visualizationType === "fixed" && (
            <Grid container mt={1} mb={2} justifyContent="center">
              {fixedValues.map(({ value: v, label }) => (
                <MDBox m={1} key={label}>
                  <MDButton color="info" onClick={() => setAnalogValue(v)}>
                    <MDTypography color="white" textTransform="capitalize">
                      {label}
                    </MDTypography>
                  </MDButton>
                </MDBox>
              ))}
            </Grid>
          )}

          {visualizationType === "value" && (
            <MDBox mt={1} mx={2}>
              <MDSlider
                value={analogValue}
                onChange={(v) => setAnalogValue(v)}
                min={parseFloat(minValue)}
                max={parseFloat(maxValue)}
                step={parseFloat(slowStep)}
                color="error"
                valueLabelDisplay="auto"
              />
            </MDBox>
          )}

          <MDBox mb={1} display="flex" justifyContent="center">
            <MDTypography variant="h2">
              {visualizationType === "value"
                ? `${analogValue?.toFixed(decimalPlaces)} ${sufix}`
                : fixedValues
                    .find((f) => parseInt(f.analogValue, 10) === Math.round(analogValue))
                    ?.label.toUpperCase() || t("noMatch").toUpperCase()}
            </MDTypography>
          </MDBox>
        </MDBox>
      );
    }

    if (isDeviceRGB) {
      return (
        <TabContext value={rgbTab}>
          <Tabs
            value={rgbTab}
            onChange={(_, v) => setRgbTab(v)}
            textColor="inherit"
            indicatorColor="secondary"
            variant="fullWidth"
          >
            <Tab label={t("color").toUpperCase()} value="color" />
            <Tab label={t("intensity").toUpperCase()} value="int" />
            <Tab label={t("whiteL").toUpperCase()} value="white" disabled={!hasWhite} />
          </Tabs>

          <TabPanel value="color" sx={{ px: 1 }}>
            {[
              { label: "R", val: rVal, setter: setRVal },
              { label: "G", val: gVal, setter: setGVal },
              { label: "B", val: bVal, setter: setBVal },
            ].map(({ label, val, setter }) => (
              <MDBox key={label} mt={2} mx={2}>
                <MDBox display="flex" justifyContent="space-between">
                  <MDTypography variant="h6">{label}</MDTypography>
                  <MDTypography variant="h6">{val}%</MDTypography>
                </MDBox>
                <MDSlider value={val} onChange={(v) => setter(v)} min={0} max={100} color="info" />
              </MDBox>
            ))}
          </TabPanel>
          <TabPanel value="int" sx={{ px: 3, textAlign: "center" }}>
            <MDSlider
              value={intensityVal}
              onChange={(v) => setIntensityVal(v)}
              min={0}
              max={100}
              color="info"
            />
            <MDTypography variant="h4">{intensityVal}%</MDTypography>
          </TabPanel>

          <TabPanel value="white" sx={{ px: 3, textAlign: "center" }}>
            <MDSlider
              value={whiteVal}
              onChange={(v) => setWhiteVal(v)}
              min={0}
              max={100}
              color="info"
            />
            <MDTypography variant="h4">{whiteVal}%</MDTypography>
          </TabPanel>
        </TabContext>
      );
    }

    return <div />;
  };

  const getDevice = () => (
    <Card sx={{ height: "100%", overflow: "hidden" }}>
      <MDBox
        p={3}
        height="100%"
        variant="gradient"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        sx={({ palette: { background } }) => ({ background: darkMode && background.card })}
      >
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          {getIcon()}

          {isDeviceRGB && (
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                marginLeft: 8,
                backgroundColor:
                  whiteVal > 0
                    ? "rgb(0,0,0)"
                    : `rgb(${pctToByte(rVal)}, ${pctToByte(gVal)}, ${pctToByte(bVal)})`,
              }}
            />
          )}

          <MDBox mt={-0.5} mr={-1}>
            {getControls()}
          </MDBox>
        </MDBox>

        {getValue()}

        <MDBox mt={1} textAlign="center">
          <MDTypography variant="body2" textTransform="capitalize">
            {deviceToEdit?.name?.toUpperCase() || ""}
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  );

  const weekDayPicker = () => (
    <Grid container display="flex" justifyContent="center">
      {t("weekDays", { returnObjects: true }).map((element, index) => (
        <Grid m={0.5} key={element}>
          <MDButton
            color={weekDay.includes(index + 1) ? "info" : "dark"}
            onClick={() => {
              if (weekDay.includes(index + 1)) {
                change(
                  "weekDay",
                  weekDay.filter((e) => e !== index + 1)
                );
              } else {
                const newWeekDays = [...weekDay, index + 1];
                change("weekDay", [...newWeekDays.sort()]);
              }
            }}
          >
            <MDTypography color="white" textTransform="capitalize">
              {element?.toUpperCase()}
            </MDTypography>
          </MDButton>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <MDBox py={3} width="100%">
      <form onSubmit={() => {}}>
        <Card id="basic-info" sx={{ overflow: "visible", width: "100%" }}>
          <MDBox
            height="4rem"
            bgColor="info"
            variant="gradient"
            coloredShadow="info"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            color="white"
            mt={-3}
            mx={10}
            px={2}
          >
            {t(actionType)} {t("device")}
          </MDBox>

          <Grid container justifyContent="center" alignItems="center" sx={{ overflow: "visible" }}>
            <Grid size={{ xs: 12 }}>
              <Grid container px={2}>
                <Grid mt={2} xs={12}>
                  <Grid
                    container
                    spacing={1}
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Grid size={{ xs: 12 }}>{weekDayPicker()}</Grid>

                    <Grid size={{ xs: 12, sm: 6 }} mx={1}>
                      <Field name="eventTime" component={FormTimePicker} label={t("time")} />
                    </Grid>

                    <Grid size={{ xs: 12 }}>{getDevice()}</Grid>

                    <Grid>
                      <Field
                        name="scheduled"
                        component={FormCheckbox}
                        label={t("scheduled").toUpperCase()}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <MDBox display="flex" justifyContent="center" m={2}>
                    <MDButton variant="gradient" color="dark" size="small" onClick={submitData}>
                      {t("Save")}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Card>
      </form>
    </MDBox>
  );
}

EventAddEditForm.defaultProps = {
  devices: [],
  weekDay: [],
  eventTime: "",
  selectedEvent: {},
  scheduled: false,
  thermostatIPReducer: {},
};

EventAddEditForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  actionType: PropTypes.string.isRequired,
  devices: PropTypes.array,
  weekDay: PropTypes.array,
  scheduled: PropTypes.bool,
  deviceNodeId: PropTypes.string.isRequired,
  eventTime: PropTypes.any,
  selectedEvent: PropTypes.object,
  thermostatIPReducer: PropTypes.object,
};

const selector = formValueSelector("EventAddEditForm");

const mapStateToProps = (state, props) => {
  const { selectedEvent } = props;
  const { hour, minute } = selectedEvent;
  const time = moment();
  if (hour && minute) {
    time.hour(hour);
    time.minute(minute);
  }
  return {
    weekDay: selector(state, "weekDay"),
    eventTime: selector(state, "eventTime"),
    scheduled: selector(state, "scheduled"),
    thermostatIPReducer: state.thermostatIPReducer || {},
    initialValues: {
      scheduled: true,
      ...selectedEvent,
      eventTime: time,
    },
  };
};

export default connect(mapStateToProps)(
  reduxForm({
    form: "EventAddEditForm", // a unique identifier for this form
    validate,
  })(EventAddEditForm)
);
